import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { useUsers } from '@/hooks/use-api'

export interface MarketingLostDecision {
  decision: 'Nurture' | 'Contact' | 'Dead'
  recheckDate?: string
  bdrAssigneeId?: string
}

interface ClosedLostModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (reason: string, details: string | undefined, marketing: MarketingLostDecision) => void
  isPending?: boolean
}

export function ClosedLostModal({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
}: ClosedLostModalProps) {
  const closedLostReasons = usePicklistOptions('opportunity', 'closed_lost_reason')
  const { data: usersResp } = useUsers({ limit: 500 })
  const bdrs = (usersResp?.data ?? []).filter((u) => u.user_type === 'BDR')
  const [reason, setReason] = useState('')
  const [details, setDetails] = useState('')
  const [decision, setDecision] = useState<'' | 'Nurture' | 'Contact' | 'Dead'>('')
  const [recheckDate, setRecheckDate] = useState('')
  const [bdrAssigneeId, setBdrAssigneeId] = useState('')
  const [error, setError] = useState('')

  const reset = () => {
    setReason('')
    setDetails('')
    setDecision('')
    setRecheckDate('')
    setBdrAssigneeId('')
    setError('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!reason) {
      setError('Please select a reason for closing as lost')
      return
    }
    if (!decision) {
      setError("Please choose what happens to the contact's Marketing Status")
      return
    }
    if (decision === 'Nurture' && !recheckDate) {
      setError('Please enter a recheck date for the Nurture task')
      return
    }

    onConfirm(reason, details.trim() || undefined, {
      decision,
      recheckDate: decision === 'Nurture' ? recheckDate : undefined,
      bdrAssigneeId: decision === 'Nurture' ? bdrAssigneeId || undefined : undefined,
    })
    reset()
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) reset()
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" aria-describedby="closed-lost-description">
        <DialogHeader>
          <DialogTitle>Close Opportunity as Lost</DialogTitle>
          <DialogDescription id="closed-lost-description">
            Please select a reason for closing this opportunity as lost.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="closed-lost-reason" className="required">
              Reason *
            </Label>
            <select
              id="closed-lost-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                if (error) setError('')
              }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-required="true"
              aria-invalid={!!error}
              aria-describedby={error ? 'closed-lost-reason-error' : undefined}
            >
              <option value="">Select a reason...</option>
              {closedLostReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {error && (
              <p
                id="closed-lost-reason-error"
                className="text-sm text-destructive mt-1"
                role="alert"
              >
                {error}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="closed-lost-details">Details</Label>
            <Textarea
              id="closed-lost-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Additional details (optional)"
              rows={3}
              aria-label="Loss details"
            />
          </div>

          {/* Marketing Status decision — mandatory (spec: Lost prompt) */}
          <div>
            <Label htmlFor="closed-lost-marketing" className="required">
              Contact&apos;s Marketing Status *
            </Label>
            <select
              id="closed-lost-marketing"
              value={decision}
              onChange={(e) => {
                setDecision(e.target.value as 'Nurture' | 'Contact' | 'Dead' | '')
                if (error) setError('')
              }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-required="true"
            >
              <option value="">Select what happens to the contact...</option>
              <option value="Nurture">
                Nurture — future potential (creates a BDR recheck task)
              </option>
              <option value="Contact">
                Contact — reset to earliest stage, re-enters marketing
              </option>
              <option value="Dead">
                Dead — no further value (sets Contact Status to Inactive)
              </option>
            </select>
          </div>

          {decision === 'Nurture' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="closed-lost-recheck" className="required">
                  Recheck date *
                </Label>
                <Input
                  id="closed-lost-recheck"
                  type="date"
                  value={recheckDate}
                  onChange={(e) => {
                    setRecheckDate(e.target.value)
                    if (error) setError('')
                  }}
                  aria-required="true"
                />
              </div>
              <div>
                <Label htmlFor="closed-lost-bdr">BDR (task assignee)</Label>
                <select
                  id="closed-lost-bdr"
                  value={bdrAssigneeId}
                  onChange={(e) => setBdrAssigneeId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">
                    {bdrs.length ? 'Assign to opportunity owner' : 'Opportunity owner'}
                  </option>
                  {bdrs.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
              aria-label="Cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={isPending}
              aria-label={isPending ? 'Closing opportunity...' : 'Confirm closed lost'}
            >
              {isPending ? 'Closing...' : 'Close as Lost'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
